import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useContext,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FieldRenderProps, useForm } from 'react-final-form';
import { isEmail, isEnterPressed } from 'utils';
import { Button } from 'ncoded-component-library';
import XIcon from 'icons/X.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  COMMA_KEY,
  ENTER_KEY,
  SEMICOLON_KEY,
  SPACE_KEY,
  TAB_KEY,
} from 'data/global';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './TextareaEmailsField.styles.scss';
import '../TextAreaField/TextAreaField.styles.scss';

const SEPARATION_CHARS = [
  ENTER_KEY,
  SPACE_KEY,
  COMMA_KEY,
  SEMICOLON_KEY,
  TAB_KEY,
];

type TextareaEmailsFieldProps = FieldWrapperProps &
  FieldRenderProps<string[], HTMLElement>;

const TextareaEmailsField: React.FC<TextareaEmailsFieldProps> = (props) => {
  const {
    className,
    required,
    meta: { touched },
    input: { value: formValue, name, onChange, ...restInput },
    onBlur,
    ...rest
  } = props;
  const { change } = useForm();
  const { t } = useTranslation();

  const { popError } = useContext(PopNotificationsContext);

  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>();

  const classes = classNames('textarea-emails-field', className);

  const hasError = useMemo(
    () => touched && !formValue.length && !isEmail(value),
    [touched, formValue.length, value],
  );

  const addEmail = useCallback(
    (email: string) => {
      setValue('');

      if (!email || !isEmail(email)) {
        popError(t('pleaseEnterValidEmailAddress'));
        return;
      }

      if (!formValue?.some((val) => email === val))
        onChange([...formValue, email]);
    },
    [formValue, onChange, popError, t],
  );

  const removeEmail = useCallback(
    (email: string) => {
      onChange(formValue.filter((val) => val !== email));
    },
    [formValue, onChange],
  );

  const handleOnKeyDown = useCallback(
    (e) => {
      if ((SEPARATION_CHARS.includes(e.code) && value) || isEnterPressed(e)) {
        e.preventDefault();
      }
    },
    [value],
  );

  const handleOnKeyUp = useCallback(
    (e) => {
      if (!SEPARATION_CHARS.includes(e.code)) return;

      if (value) {
        addEmail(value.trim());
      }
    },
    [addEmail, value],
  );

  const RenderTags = useMemo(
    () =>
      formValue.map((val) => (
        <div key={val} className="tag">
          <span>{val}</span>
          <Button
            variant="icon"
            onClick={() => removeEmail(val)}
            icon={XIcon}
          />
        </div>
      )),
    [formValue, removeEmail],
  );

  useEffect(() => {
    change(`${name}Unsubmited`, value);
  }, [change, name, value]);

  return (
    <FieldWrapper
      className={classes}
      required={required}
      hasError={hasError && required}
      error={t('requiredField')}
      {...rest}
    >
      <div className="emails-textarea">
        {!!formValue?.length && (
          <div className="emails-textarea__container">{RenderTags}</div>
        )}

        <input
          type="email"
          ref={inputRef}
          value={value}
          onChange={({ target }) => setValue(target.value)}
          onKeyUp={handleOnKeyUp}
          onKeyDown={handleOnKeyDown}
          onBlur={(e) => {
            if (!e.relatedTarget && value) {
              addEmail(value.trim());
            }
            onBlur(e);
          }}
          {...restInput}
          {...rest}
        />
      </div>
    </FieldWrapper>
  );
};

export default TextareaEmailsField;
